/**
 * Live presence on a workflow's chart (AMO-178): who else has it open and
 * what they are doing, and this person's own share of it, over the
 * environment's WebSocket (`workflows.subscribePresence` / `pushPresence`).
 * The room's word on the workflow (each `sync`, every `revision`) goes on to
 * the chart's own state (`useWorkflowDoc`), which asks for versions it
 * missed with `workflows.revisionsSince` on the same subscription.
 *
 * The channel's rules (throttling, reconnects, retries) are in
 * `./workflowPresenceChannel.ts`; this module is its WebSocket and the hook.
 *
 * @module state/workflowPresence
 */
import { useAtomValue } from "@effect/atom-react";
import { EnvironmentSupervisor } from "@amoreg/client-runtime/connection";
import { subscribe } from "@amoreg/client-runtime/rpc";
import {
  createEnvironmentRpcCommand,
  runAtomCommand,
  runInEnvironment,
  squashAtomCommandFailure,
} from "@amoreg/client-runtime/state/runtime";
import {
  type EnvironmentId,
  type WorkflowPeer,
  type WorkflowPresenceRef,
  type WorkflowPresenceState,
  type WorkflowRevisionsSinceResult,
  WS_METHODS,
} from "@amoreg/contracts";
import * as Cause from "effect/Cause";
import * as Effect from "effect/Effect";
import * as Option from "effect/Option";
import * as Stream from "effect/Stream";
import * as SubscriptionRef from "effect/SubscriptionRef";
import { useCallback, useEffect, useRef, useState } from "react";

import { connectionAtomRuntime } from "../connection/runtime";
import { appAtomRegistry } from "../rpc/atomRegistry";
import { primaryEnvironmentIdAtom } from "./primaryEnvironment";
import {
  openPresenceChannel,
  type PresenceChannel,
  type PresenceWorkflowEvent,
  type WorkflowPresenceTransport,
} from "./workflowPresenceChannel";

const pushCommand = createEnvironmentRpcCommand(connectionAtomRuntime, {
  label: "web:workflows:push-presence",
  tag: WS_METHODS.workflowsPushPresence,
});

const revisionsSinceCommand = createEnvironmentRpcCommand(connectionAtomRuntime, {
  label: "web:workflows:revisions-since",
  tag: WS_METHODS.workflowsRevisionsSince,
});

const QUIET = { reportFailure: false, reportDefect: false } as const;

/** The real transport, over one environment's RPC session (as a space document's). */
export const makeEnvironmentWorkflowPresenceTransport = (
  environmentId: EnvironmentId,
): WorkflowPresenceTransport => ({
  subscribe: (ref, handlers) => {
    const program = Effect.gen(function* () {
      const supervisor = yield* EnvironmentSupervisor;
      const connection = SubscriptionRef.changes(supervisor.session).pipe(
        Stream.map(Option.isSome),
        Stream.changes,
        Stream.runForEach((online) => Effect.sync(() => handlers.onConnection(online))),
      );
      const events = subscribe(WS_METHODS.workflowsSubscribePresence, ref).pipe(
        Stream.runForEach((event) => Effect.sync(() => handlers.onEvent(event))),
      );
      yield* Effect.all([events, connection], { concurrency: "unbounded", discard: true });
    });
    const atom = connectionAtomRuntime.atom(
      runInEnvironment(environmentId, program).pipe(
        Effect.catchCause((cause) =>
          Cause.hasInterruptsOnly(cause)
            ? Effect.void
            : Effect.sync(() => handlers.onError(Cause.squash(cause))),
        ),
      ),
    );
    return appAtomRegistry.mount(atom);
  },
  push: async (input) => {
    const result = await runAtomCommand(
      appAtomRegistry,
      pushCommand,
      { environmentId, input },
      QUIET,
    );
    if (result._tag === "Failure") throw squashAtomCommandFailure(result);
  },
});

/** A catch-up's answer as it came (decoding it is the caller's), or why there is none. */
export type RevisionsSinceOutcome =
  | { readonly kind: "answered"; readonly result: WorkflowRevisionsSinceResult }
  | { readonly kind: "failed"; readonly retryAfterMs: number | null };

export interface WorkflowPresenceView {
  readonly peers: ReadonlyArray<WorkflowPeer>;
  readonly publish: (patch: Partial<WorkflowPresenceState>) => void;
  readonly onWorkflowEvent: (listener: (event: PresenceWorkflowEvent) => void) => () => void;
  /** The versions after `since` on this chart's subscription; fails while it has none. */
  readonly revisionsSince: (since: number) => Promise<RevisionsSinceOutcome>;
}

const NO_PEERS: ReadonlyArray<WorkflowPeer> = [];

/** Who else is on this chart, and this person's own share of it; nothing while `ref` is null. */
export function useWorkflowPresence(ref: WorkflowPresenceRef | null): WorkflowPresenceView {
  const environmentId = useAtomValue(primaryEnvironmentIdAtom);
  const [peers, setPeers] = useState<ReadonlyArray<WorkflowPeer>>(NO_PEERS);
  const channel = useRef<PresenceChannel | null>(null);
  const organizationId = ref?.organizationId ?? null;
  const workflowId = ref?.workflowId ?? null;
  const runId = ref?.runId ?? null;
  const listeners = useRef(new Set<(event: PresenceWorkflowEvent) => void>());
  // The editing chart's subscription key, for catch-up; a run's chart has none to offer.
  const connection = useRef<string | null>(null);

  useEffect(() => {
    if (!environmentId || !organizationId || !workflowId) return;
    const opened = openPresenceChannel(makeEnvironmentWorkflowPresenceTransport(environmentId), {
      organizationId,
      workflowId,
      runId,
    });
    channel.current = opened;
    const stop = opened.onPeers(() => setPeers(opened.peers()));
    const stopWorkflowEvents = opened.onWorkflowEvent((event) => {
      if (event.type === "sync") connection.current = event.version === null ? null : event.connection;
      else if (event.type === "lost") connection.current = null;
      for (const listener of listeners.current) listener(event);
    });
    return () => {
      stop();
      stopWorkflowEvents();
      opened.destroy();
      if (channel.current === opened) channel.current = null;
      connection.current = null;
      setPeers(NO_PEERS);
    };
  }, [environmentId, organizationId, workflowId, runId]);

  const publish = useCallback(
    (patch: Partial<WorkflowPresenceState>) => channel.current?.publish(patch),
    [],
  );
  const onWorkflowEvent = useCallback((listener: (event: PresenceWorkflowEvent) => void) => {
    listeners.current.add(listener);
    return () => {
      listeners.current.delete(listener);
    };
  }, []);
  const revisionsSince = useCallback(
    async (since: number): Promise<RevisionsSinceOutcome> => {
      const key = connection.current;
      if (!environmentId || !organizationId || !workflowId || key === null) return { kind: "failed", retryAfterMs: null };
      const result = await runAtomCommand(
        appAtomRegistry,
        revisionsSinceCommand,
        { environmentId, input: { organizationId, workflowId, connection: key, since } },
        QUIET,
      );
      if (result._tag === "Success") return { kind: "answered", result: result.value };
      const error = squashAtomCommandFailure(result) as { readonly _tag?: unknown; readonly retryAfterMs?: unknown } | null;
      return {
        kind: "failed",
        retryAfterMs: error?._tag === "WorkflowCatchUpRateLimitedError" && typeof error.retryAfterMs === "number" ? error.retryAfterMs : null,
      };
    },
    [environmentId, organizationId, workflowId],
  );
  return { peers, publish, onWorkflowEvent, revisionsSince };
}
